import React, { useMemo, useState } from "react"

import { ClipboardList } from "lucide-react"
import { AlertCircle } from "lucide-react"

import DiaryEntryCard from "@/components/people/diary/DiaryEntryCard"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { useInfiniteDiaryEntries } from "@/hooks/useFetchDiaryEntries"
import { useUpdateDiaryEntry } from "@/hooks/useUpdateDiaryEntry"
import { DiaryEntry } from "@/types/person"

import { compareDateKeys, formatDateHeader, toDateKey, toTimestamp } from "./diaryEntryDates"

const LIMIT = 30

interface DiaryEntriesListProps {
  // Outstanding means an unresolved follow-up: an action was set and nobody has completed it
  showOnlyOutstanding?: boolean
}

const DiaryEntriesList = ({ showOnlyOutstanding = true }: DiaryEntriesListProps) => {
  // --- Filter/search/sort state ---
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc")
  const [personQuery, setPersonQuery] = useState("")
  const [selectedPersonId, setSelectedPersonId] = useState<string>("")
  const [tag, setTag] = useState<string>("_any")
  const [subTag, setSubTag] = useState<string>("_any")

  // --- Query - resets/infinite scroll handling ---
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteDiaryEntries({
      hasFollowUpAction: showOnlyOutstanding ? true : undefined,
      actionCompleted: showOnlyOutstanding ? false : undefined,
      fetchAll: !showOnlyOutstanding,
      limit: LIMIT,
    })

  // --- Diary/card helpers ---
  const getTagColor = (tag: string) => {
    switch (tag) {
      case "Serious Escalation":
        return "bg-red-100 text-red-800"
      case "Service Review & Config":
        return "bg-blue-100 text-blue-800"
      case "Driver Feedback":
        return "bg-green-100 text-green-800"
      case "No Show Call":
        return "bg-yellow-100 text-yellow-800"
      case "General Call":
        return "bg-palette-jungleGreen100 text-palette-jungleGreen800"
      case "Compliance Violation":
        return "bg-purple-100 text-purple-800"
      case "Scheduling":
        return "bg-orange-100 text-orange-800"
      case "Onboarding Admin":
        return "bg-cyan-100 text-cyan-800"
      case "Off-Boarding":
        return "bg-pink-100 text-pink-800"
      case "THD Check-in":
        return "bg-indigo-100 text-indigo-800"
      case "New Driver Hypercare":
        return "bg-teal-100 text-teal-800"
      case "Contractor Ended Engagement":
        return "bg-fuchsia-100 text-fuchsia-800"
      case "Unnotified Absence (UA) Calls":
        return "bg-lime-100 text-lime-800"
      default:
        return "bg-palette-jungleGreen100 text-palette-jungleGreen800"
    }
  }
  // Get display entry ID (same as used in DiaryEntryCard)
  const getDisplayEntryId = (entryId: string) => {
    if (!entryId) return entryId
    const parts = entryId.split("-")
    // If first part looks like DD/MM/YYYY, drop it
    if (parts.length > 1 && /^\d{2}\/\d{2}\/\d{4}$/.test(parts[0])) {
      return parts.slice(1).join("-")
    }
    return entryId
  }

  // Get all loaded entries (before frontend filtering)
  const allLoadedEntries: DiaryEntry[] = useMemo(
    () => data?.pages.flatMap((pg) => pg.data) || [],
    [data?.pages],
  )

  // Calculate tag counts from all loaded entries (before filtering)
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    allLoadedEntries.forEach((entry) => {
      const entryTag = entry.driverOpsTag || "Other"
      counts[entryTag] = (counts[entryTag] || 0) + 1
    })
    return counts
  }, [allLoadedEntries])

  // Apply frontend filters (tag, subtag, and entryId)
  const filteredEntries = useMemo(() => {
    let filtered = [...allLoadedEntries]

    // Filter by tag
    if (tag !== "_any") {
      filtered = filtered.filter((entry) => entry.driverOpsTag === tag)
    }

    // Filter by subtag
    if (subTag !== "_any") {
      filtered = filtered.filter((entry) => {
        if (Array.isArray(entry.driverOpsSubTag)) {
          return entry.driverOpsSubTag.some((st) => st === subTag)
        }
        return entry.driverOpsSubTag === subTag
      })
    }

    // Filter by entryId (split title format) - frontend only
    if (selectedPersonId) {
      filtered = filtered.filter((entry) => {
        const displayId = getDisplayEntryId(entry.entryId)
        return displayId === selectedPersonId
      })
    }

    // Filter by search query (entryId or content)
    if (personQuery) {
      const query = personQuery.toLowerCase()
      filtered = filtered.filter((entry) => {
        const displayId = getDisplayEntryId(entry.entryId).toLowerCase()
        const content = entry.content?.toLowerCase() || ""
        return displayId.includes(query) || content.includes(query)
      })
    }

    return filtered
  }, [allLoadedEntries, tag, subTag, selectedPersonId, personQuery])

  // Sort filtered entries
  let entries: DiaryEntry[] = filteredEntries
  if (sortOrder === "asc")
    entries = [...entries].sort(
      (a, b) => (toTimestamp(a.callDateTime) ?? 0) - (toTimestamp(b.callDateTime) ?? 0),
    )
  else
    entries = [...entries].sort(
      (a, b) => (toTimestamp(b.callDateTime) ?? 0) - (toTimestamp(a.callDateTime) ?? 0),
    )

  // Get all unique tags from entries, sorted by count (descending)
  const sortedTags = useMemo(() => {
    return Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a])
  }, [tagCounts])

  // Get unique entry IDs (split title format) from loaded entries with counts
  const entryIdCounts = useMemo(() => {
    const counts: Record<string, { displayId: string; fullId: string; count: number }> = {}
    allLoadedEntries.forEach((entry) => {
      const displayId = getDisplayEntryId(entry.entryId)
      if (!counts[displayId]) {
        counts[displayId] = {
          displayId: displayId,
          fullId: entry.entryId,
          count: 0,
        }
      }
      counts[displayId].count++
    })
    return counts
  }, [allLoadedEntries])

  // Get sorted entry IDs list
  const sortedEntryIds = useMemo(() => {
    return Object.values(entryIdCounts).sort((a, b) => b.count - a.count)
  }, [entryIdCounts])

  // Group entries by date
  const groupedEntriesByDate = useMemo(() => {
    const grouped: Record<string, DiaryEntry[]> = {}
    entries.forEach((entry) => {
      const dateKey = toDateKey(entry.callDateTime)
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(entry)
    })

    // Sort entries within each date group by time
    Object.keys(grouped).forEach((dateKey) => {
      if (sortOrder === "asc") {
        grouped[dateKey].sort(
          (a, b) => (toTimestamp(a.callDateTime) ?? 0) - (toTimestamp(b.callDateTime) ?? 0),
        )
      } else {
        grouped[dateKey].sort(
          (a, b) => (toTimestamp(b.callDateTime) ?? 0) - (toTimestamp(a.callDateTime) ?? 0),
        )
      }
    })

    return grouped
  }, [entries, sortOrder])

  // Format date for display
  // Get sorted date keys
  const sortedDateKeys = useMemo(() => {
    return Object.keys(groupedEntriesByDate).sort((a, b) => compareDateKeys(a, b, sortOrder))
  }, [groupedEntriesByDate, sortOrder])

  const updateDiaryEntryMutation = useUpdateDiaryEntry()
  const handleConfirmComplete = (entry: DiaryEntry) =>
    updateDiaryEntryMutation.mutate({
      id: entry.id, // Supabase UUID
      followUpComplete: true,
    })

  const handleTagClick = (selectedTag: string) => {
    if (selectedTag === tag) {
      // If clicking the same tag, clear the filter
      setTag("_any")
    } else {
      setTag(selectedTag)
      setSubTag("_any") // Reset subtag when changing tag
    }
  }

  // --- UI ---
  return (
    <div className="bg-white rounded-lg shadow-sm h-[calc(100vh-96px)] flex flex-col">
      {/* Header/section title */}
      <div className="p-4 border-b border-palette-jungleGreen200 flex items-center gap-2">
        <ClipboardList className="h-7 w-7 text-blue-400" />
        <h2 className="text-xl font-semibold text-palette-jungleGreen900">
          {showOnlyOutstanding ? "Outstanding Diary Entries" : "All Diary Entries"} (
          {entries.length})
        </h2>
      </div>
      {/* --- Main content --- */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Main diary entries panel */}
        <div className="flex-1 bg-white px-0 py-0 overflow-y-auto flex flex-col">
          {/* Search, Sort, and Filter by Tag grouped together */}
          {!isLoading && allLoadedEntries.length > 0 && (
            <div className="bg-palette-jungleGreen000 border-b-2 border-palette-jungleGreen300 px-4 py-4 mb-4 shadow-sm">
              {/* Search, Filter by Entry ID, and Sort bar */}
              <div className="mb-4 flex items-center gap-4">
                <div className="flex-1 relative">
                  <Input
                    type="search"
                    className="rounded-md border-palette-jungleGreen300"
                    placeholder="Search by entry ID or content..."
                    value={personQuery}
                    onChange={(e) => {
                      setPersonQuery(e.target.value)
                      if (!e.target.value) setSelectedPersonId("")
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-palette-jungleGreen700 whitespace-nowrap">
                    Filter by Entry ID:
                  </label>
                  <Select
                    value={selectedPersonId || "_any"}
                    onValueChange={(val) => {
                      if (val === "_any") {
                        setSelectedPersonId("")
                      } else {
                        setSelectedPersonId(val)
                      }
                    }}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="All Entries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">All Entries ({allLoadedEntries.length})</SelectItem>
                      {sortedEntryIds.map((entryId) => (
                        <SelectItem key={entryId.displayId} value={entryId.displayId}>
                          {entryId.displayId} ({entryId.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-palette-jungleGreen700 whitespace-nowrap">
                    Sort by Date:
                  </label>
                  <Select
                    value={sortOrder}
                    onValueChange={(v) => setSortOrder(v as "desc" | "asc")}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Newest First</SelectItem>
                      <SelectItem value="asc">Oldest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Clickable tag badges */}
              <div>
                <div className="mb-2">
                  <span className="text-xs font-semibold text-palette-jungleGreen600 uppercase tracking-wide">
                    Filter by Tag
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* All badge */}
                  <Badge
                    variant={tag === "_any" ? "default" : "outline"}
                    className={`cursor-pointer px-3 py-1.5 text-sm font-medium transition-all shadow-sm ${
                      tag === "_any"
                        ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md"
                        : "bg-white text-palette-jungleGreen700 hover:bg-palette-jungleGreen000 border-palette-jungleGreen300"
                    }`}
                    onClick={() => handleTagClick("_any")}
                  >
                    All ({allLoadedEntries.length})
                  </Badge>
                  {/* Tag badges */}
                  {sortedTags.map((entryTag) => (
                    <Badge
                      key={entryTag}
                      variant={tag === entryTag ? "default" : "outline"}
                      className={`cursor-pointer px-3 py-1.5 text-sm font-medium transition-all shadow-sm ${
                        tag === entryTag
                          ? `${getTagColor(entryTag)} border-2 border-palette-jungleGreen600 shadow-md`
                          : `${getTagColor(entryTag)} hover:opacity-90 border-palette-jungleGreen200`
                      }`}
                      onClick={() => handleTagClick(entryTag)}
                    >
                      {entryTag} ({tagCounts[entryTag]})
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner className="mr-2" /> Loading...
            </div>
          )}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Error loading outstanding diary entries:{" "}
                {error instanceof Error ? error.message : "Unknown error"}
              </AlertDescription>
            </Alert>
          )}
          {!isLoading && entries.length === 0 && (
            <div className="flex items-center justify-center text-palette-jungleGreen400 py-16">
              <ClipboardList className="h-10 w-10 mr-3 text-blue-100" />
              <span>
                {showOnlyOutstanding
                  ? "No outstanding diary actions found with current filter."
                  : "No diary entries found with current filter."}
              </span>
            </div>
          )}
          <div className="w-full">
            {sortedDateKeys.map((dateKey) => (
              <div key={dateKey} className="mb-8">
                <div className="sticky top-0 bg-white z-10 py-4 px-4 border-b-2 border-palette-jungleGreen400 shadow-sm">
                  <div className="flex items-baseline gap-3">
                    <h3 className="text-xl font-bold text-palette-jungleGreen900">
                      {formatDateHeader(dateKey)}
                    </h3>
                    <span className="text-sm font-medium text-palette-jungleGreen600 bg-palette-jungleGreen100 px-2 py-1 rounded">
                      {groupedEntriesByDate[dateKey].length}{" "}
                      {groupedEntriesByDate[dateKey].length === 1 ? "entry" : "entries"}
                    </span>
                  </div>
                </div>
                <div className="space-y-4 px-4 pt-4">
                  {groupedEntriesByDate[dateKey].map((entry) => (
                    <DiaryEntryCard
                      key={entry.id}
                      entry={entry}
                      onResolve={handleConfirmComplete}
                      pendingUpdate={
                        updateDiaryEntryMutation.isPending &&
                        updateDiaryEntryMutation.variables?.id === entry.id
                      }
                      compactSpacing={true}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Load more */}
          {hasNextPage && (
            <div className="flex justify-center py-4">
              <Button size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? (
                  <>
                    <Spinner className="mr-2" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DiaryEntriesList
